<script lang="ts">
  import { faExpand, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
  import { onMount, tick, untrack } from 'svelte';
  import { historyKey } from '$lib/core/interaction';
  import { RemoteCommentStore } from '$lib/services/comment-store';
  import { RemoteDocumentStore } from '$lib/services/document-store';
  import { LocalPreferences } from '$lib/services/preferences';
  import { loadBrowserScriptRuntime } from '$lib/services/script-runtime';
  import { safeStorage } from '$lib/services/storage';
  import { CommentSession, provideComments } from '../comment-session.svelte';
  import { DocumentSession } from '../document-session.svelte';
  import { EditorState } from '../editor-state.svelte';
  import { PANEL_WIDTH, PanelSections, SCRIPT_WIDTH, SidePanel } from '../panel-sections.svelte';
  import { Prompts } from '../prompts.svelte';
  import CanvasStage from './CanvasStage.svelte';
  import ConnectAI from './ConnectAI.svelte';
  import ConfirmDialog from './ConfirmDialog.svelte';
  import DocumentBar from './DocumentBar.svelte';
  import DownloadPanel from './DownloadPanel.svelte';
  import Icon from './Icon.svelte';
  import LeftPanel from './LeftPanel.svelte';
  import PropertiesCard from './PropertiesCard.svelte';
  import RightPanel from './RightPanel.svelte';
  import { LEFT_PANEL_SECTIONS } from './left-panel-sections';
  import { RIGHT_PANEL_INITIAL, RIGHT_PANEL_SECTIONS } from './right-panel-sections';
  import StatusBar from './StatusBar.svelte';
  import ToolPalette from './ToolPalette.svelte';

  /** Who is signed in, from the app around the editor; absent in tests. */
  const {
    account,
  }: { account?: { email: string; signOut(): void; renew(): Promise<void>; expired(): void } } =
    $props();

  const prompts = new Prompts();
  const editor = new EditorState(prompts.confirm);
  const session = new DocumentSession(
    editor,
    prompts.confirm,
    new RemoteDocumentStore(safeStorage(), untrack(() => account) ?? null),
  );
  const comments = new CommentSession(
    editor,
    prompts.confirm,
    () => session.saveNow(),
    new RemoteCommentStore(safeStorage(), untrack(() => account) ?? null),
  );
  provideComments(comments);

  const preferences = new LocalPreferences();
  const leftPanel = new PanelSections(
    'left',
    PANEL_WIDTH,
    LEFT_PANEL_SECTIONS.map((section) => section.id),
    preferences,
  );
  const rightPanel = new PanelSections(
    'right',
    SCRIPT_WIDTH,
    RIGHT_PANEL_SECTIONS.map((section) => section.id),
    preferences,
    RIGHT_PANEL_INITIAL,
  );
  // Two open columns leave too little room for the canvas.
  SidePanel.oneOpenAmong(leftPanel, rightPanel);

  const TEXT_INPUT_TYPES = ['text', 'number', 'search', 'email', 'url', 'tel', 'password'];

  /**
   * Only text entry swallows shortcuts; a focused checkbox must not. An element marked
   * `data-own-keys`, such as the code editor, whose input is neither a field nor
   * contenteditable, keeps every key typed inside it.
   */
  function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable || target.closest('[data-own-keys]')) return true;
    if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
    return target instanceof HTMLInputElement && TEXT_INPUT_TYPES.includes(target.type);
  }

  const keyInput = (event: KeyboardEvent) => ({
    key: event.key,
    meta: event.metaKey || event.ctrlKey,
    shift: event.shiftKey,
    repeat: event.repeat,
  });

  function onKeyDown(event: KeyboardEvent) {
    if (prompts.pending) return;
    const step = historyKey(keyInput(event));
    if (step) {
      event.preventDefault();
      editor[step]();
      return;
    }
    if (isTypingTarget(event.target)) return;
    if (editor.keyDown(keyInput(event))?.preventDefault) event.preventDefault();
  }

  function onKeyUp(event: KeyboardEvent) {
    editor.keyUp(keyInput(event));
  }

  const WHEEL_PIXELS = [1, 40, 800];

  /**
   * A browser steps a focused number field on scroll; this scrolls what holds the field
   * instead, as it would over anything else, so a value only changes on purpose.
   */
  function onWheel(event: WheelEvent) {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || field.type !== 'number') return;
    if (field !== document.activeElement) return;
    event.preventDefault();
    const scale = WHEEL_PIXELS[event.deltaMode] ?? 1;
    let box = field.parentElement;
    while (
      box &&
      !(box.scrollHeight > box.clientHeight && /auto|scroll/.test(getComputedStyle(box).overflowY))
    ) {
      box = box.parentElement;
    }
    (box ?? document.scrollingElement)?.scrollBy(event.deltaX * scale, event.deltaY * scale);
  }

  onMount(() => {
    void session.init();
    void loadBrowserScriptRuntime().then((runtime) => editor.attachRuntime(runtime));
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('wheel', onWheel);
      session.dispose();
    };
  });

  $effect(() => {
    void editor.document.name;
    void editor.source;
    void editor.params;
    void editor.layers;
    void editor.settings;
    if (editor.documentId) session.scheduleSave();
  });

  $effect(() => session.notePlace(editor.viewport));

  $effect(() => void comments.open(editor.documentId));

  // A box marked with the comment tool is written about at once, so its panel opens with the
  // text box ready to type in.
  $effect(() => {
    // Escape on the canvas or a switch of tool drops the box, and the text written about it.
    if (!editor.commentRegion) {
      untrack(() => (comments.draft = ''));
      return;
    }
    untrack(() => {
      comments.boxMarked(editor.viewport.zoom);
      rightPanel.open('comments');
    });
    void tick().then(() => document.getElementById('comment-draft')?.focus());
  });
</script>

<svelte:window
  onkeydown={onKeyDown}
  onkeyup={onKeyUp}
  onfocus={() => {
    void session.checkForChanges();
    void comments.refresh();
  }}
  onpagehide={() => void session.leave()}
/>

<div class="editor">
  <header>
    <DocumentBar {editor} {session} />
    <ToolPalette {editor} />
    <span class="spacer"></span>
    <div class="history">
      <ConnectAI />
      <button
        type="button"
        class="icon"
        title="Fit the drawing to the view"
        onclick={() => editor.zoomToFit()}
      >
        <Icon icon={faExpand} />
        Fit
      </button>
      <DownloadPanel {editor} />
      {#if account}
        <button
          type="button"
          class="icon"
          title="Sign out of {account.email}"
          onclick={async () => {
            await session.saveNow();
            account.signOut();
          }}
        >
          <Icon icon={faRightFromBracket} />
          Sign out
        </button>
      {/if}
    </div>
  </header>

  <div class="body">
    <LeftPanel {editor} panel={leftPanel} />
    <div class="stage-area">
      <CanvasStage {editor} />
      <PropertiesCard
        {editor}
        onShowSource={(id) => {
          rightPanel.open('script');
          editor.showSource(id, true);
        }}
      />
    </div>
    <RightPanel {editor} panel={rightPanel} />
  </div>

  <StatusBar {editor} />
</div>

<ConfirmDialog {prompts} />

<style>
  .editor {
    display: grid;
    grid-template-rows: auto 1fr auto;
    height: 100vh;
  }

  header {
    box-sizing: border-box;
    height: 3.25rem;
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-2) var(--space-3);
    border-bottom: 1px solid var(--color-border);
    background: var(--color-surface);
  }

  .spacer {
    flex: 1;
  }

  .history {
    display: flex;
    gap: 4px;
  }

  .history button {
    padding: 5px 10px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font: inherit;
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .history .icon {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .history button:disabled {
    opacity: 0.45;
    cursor: default;
  }

  .body {
    display: grid;
    /* Each side column sets its own width, which changes only between interactions. */
    grid-template-columns: auto minmax(0, 1fr) auto;
    min-height: 0;
  }

  .stage-area {
    position: relative;
    min-width: 0;
    min-height: 0;
  }
</style>
